import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  HStack,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  Select,
  Checkbox,
  SimpleGrid,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Badge,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Users = () => {
  const { t } = useLanguage();
  const toast = useToast();
  const [tabIdx, setTabIdx] = useState(0);
  const [rolesSummary, setRolesSummary] = useState([]);
  const [permKeys, setPermKeys] = useState([]);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [rolePerms, setRolePerms] = useState([]);
  const allRoles = useMemo(() => ['admin','manager','user'], []);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [q, setQ] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username:'', email:'', full_name:'', department:'', role:'user', password:'' });
  
  // Entity management state
  const [allEntities, setAllEntities] = useState([]);
  const [selectedUserForEntities, setSelectedUserForEntities] = useState(null);
  const [userEntities, setUserEntities] = useState([]);
  const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          api.get('/api/users/roles'),
          api.get('/api/users/permissions/keys'),
          api.get('/api/entities').catch(() => ({ data: { data: { entities: [] } } }))
        ]);
        setRolesSummary(r1.data?.data?.roles || []);
        setPermKeys(r2.data?.data?.keys || []);
        setAllEntities(r3.data?.data?.entities || []);
      } catch (e) {
        // no-op
      }
    })();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await api.get('/api/users', { params: { q } });
      setUsers(res.data?.data?.users || []);
    } catch (e) {
      toast({ title: t('users.load_users_failed'), status: 'error' });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const loadRolePerms = async (role) => {
    try {
      const res = await api.get(`/api/users/permissions/by-role/${role}`);
      setRolePerms(res.data?.data?.permissions || []);
    } catch (e) {
      setRolePerms([]);
    }
  };

  useEffect(() => { loadRolePerms(selectedRole); }, [selectedRole]);

  const togglePerm = (key) => {
    setRolePerms((prev) => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const savePerms = async () => {
    try {
      await api.post(`/api/users/permissions/by-role/${selectedRole}`, { perm_keys: rolePerms });
      toast({ title: t('users.toast.saved'), status: 'success' });
    } catch (e) {
      toast({ title: t('users.toast.error'), status: 'error' });
    }
  };

  const openCreate = () => {
    setEditUser(null);
    setForm({ username:'', email:'', full_name:'', department:'', role:'user', password:'' });
    setIsModalOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username:u.username, email:u.email, full_name:u.full_name, department:u.department||'', role:u.role, password:'' });
    setIsModalOpen(true);
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/api/users/${u.id}`);
      toast({ title: t('users.toast.deleted'), status: 'success' });
      loadUsers();
    } catch (e) {
      toast({ title: t('users.toast.delete_failed'), status: 'error' });
    }
  };

  const submitUser = async () => {
    try {
      if (editUser) {
        await api.put(`/api/users/${editUser.id}`, { username: form.username, email: form.email, full_name: form.full_name, department: form.department, role: form.role, is_active: 1 });
      } else {
        await api.post('/api/users', { ...form });
      }
      setIsModalOpen(false);
      loadUsers();
      toast({ title: t('users.toast.saved'), status: 'success' });
    } catch (e) {
      toast({ title: t('users.save_failed'), status: 'error' });
    }
  };

  const openEntityManagement = async (user) => {
    setSelectedUserForEntities(user);
    try {
      const res = await api.get(`/api/entities/users/${user.id}/entities`);
      setUserEntities(res.data?.data?.entities || []);
      setIsEntityModalOpen(true);
    } catch (e) {
      toast({ title: t('users.load_entities_failed', { defaultValue: 'Failed to load entities' }), status: 'error' });
    }
  };

  const toggleUserEntity = async (entityId, isDefault = false) => {
    try {
      const isAssigned = userEntities.some(ue => ue.id === entityId);
      if (isAssigned) {
        // Remove entity
        await api.delete(`/api/entities/users/${selectedUserForEntities.id}/entities/${entityId}`);
        setUserEntities(prev => prev.filter(ue => ue.id !== entityId));
      } else {
        // Add entity
        await api.post(`/api/entities/users/${selectedUserForEntities.id}/entities/${entityId}`, { is_default: isDefault });
        // Reload user entities
        const res = await api.get(`/api/entities/users/${selectedUserForEntities.id}/entities`);
        setUserEntities(res.data?.data?.entities || []);
      }
      // Reload users list to update entity badges
      loadUsers();
    } catch (e) {
      toast({ title: t('users.update_entity_failed', { defaultValue: 'Failed to update entity' }), status: 'error' });
    }
  };

  const setDefaultEntity = async (entityId) => {
    try {
      await api.post(`/api/entities/users/${selectedUserForEntities.id}/entities/${entityId}`, { is_default: true });
      const res = await api.get(`/api/entities/users/${selectedUserForEntities.id}/entities`);
      setUserEntities(res.data?.data?.entities || []);
      // Reload users list to update entity badges
      loadUsers();
      toast({ title: t('users.default_entity_set', { defaultValue: 'Default entity set' }), status: 'success' });
    } catch (e) {
      toast({ title: t('users.update_entity_failed', { defaultValue: 'Failed to update entity' }), status: 'error' });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('users.user_management')}</Heading>
        <HStack>
          <Input placeholder={t('users.search_users')} value={q} onChange={(e)=> setQ(e.target.value)} size="sm" w="220px" />
          <Button size="sm" onClick={loadUsers}>{t('users.search')}</Button>
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>
            {t('users.new_user')}
          </Button>
        </HStack>
      </HStack>

      <Tabs index={tabIdx} onChange={setTabIdx} colorScheme="brand">
        <TabList>
          <Tab>{t('users.users_tab')}</Tab>
          <Tab>{t('users.groups_tab')}</Tab>
          <Tab>{t('users.permissions_tab')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Card><CardBody>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('users.full_name')}</Th>
                    <Th>{t('users.email')}</Th>
                    <Th>{t('users.department')}</Th>
                    <Th>{t('users.role')}</Th>
                    <Th>{t('users.entity_access', { defaultValue: 'Entity Access' })}</Th>
                    <Th>{t('users.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(users||[]).map(u => (
                    <Tr key={u.id}>
                      <Td>{u.full_name}</Td>
                      <Td>{u.email}</Td>
                      <Td>{u.department||'-'}</Td>
                      <Td>{u.role}</Td>
                      <Td>
                        {u.entities && u.entities.length > 0 ? (
                          <Wrap spacing={1}>
                            {u.entities.map(entity => (
                              <WrapItem key={entity.id}>
                                <Badge 
                                  colorScheme={entity.is_default === 1 || entity.is_default === true ? 'brand' : 'gray'}
                                  fontSize="xs"
                                >
                                  {entity.name}
                                  {(entity.is_default === 1 || entity.is_default === true) && ' (Default)'}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        ) : (
                          <Text fontSize="xs" color="gray.400">-</Text>
                        )}
                        <Button 
                          size="xs" 
                          variant="ghost" 
                          colorScheme="brand"
                          mt={1}
                          onClick={() => openEntityManagement(u)}
                        >
                          {t('users.manage', { defaultValue: 'Manage' })}
                        </Button>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button size="xs" variant="outline" onClick={()=> openEdit(u)}>{t('users.edit')}</Button>
                          <Button size="xs" colorScheme="red" variant="outline" onClick={()=> removeUser(u)}>{t('users.delete')}</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                  {(!users || users.length===0) && (
                    <Tr><Td colSpan={7}><Text color="gray.500">{t('users.no_users')}</Text></Td></Tr>
                  )}
                </Tbody>
              </Table>
            </CardBody></Card>
          </TabPanel>
          <TabPanel>
            <Card><CardBody>
              <Heading size="sm" mb={3}>{t('users.groups.title')}</Heading>
              <VStack align="stretch" spacing={2}>
                {rolesSummary.map((r) => (
                  <HStack key={r.role} justify="space-between" p={2} borderRadius="md" bg="gray.50">
                    <Text fontWeight="medium">{r.role}</Text>
                    <Text color="gray.500">{r.count}</Text>
                  </HStack>
                ))}
              </VStack>
            </CardBody></Card>
          </TabPanel>
          <TabPanel>
            <Card><CardBody>
              <HStack mb={4}>
                <Text fontWeight="medium">{t('users.permissions.title')}</Text>
                <Select value={selectedRole} onChange={(e)=> setSelectedRole(e.target.value)} maxW="220px">
                  {allRoles.map(r => (<option key={r} value={r}>{r}</option>))}
                </Select>
                <Button onClick={savePerms} colorScheme="brand" ml="auto">{t('users.permissions.save')}</Button>
              </HStack>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                {permKeys.map(k => (
                  <Checkbox key={k} isChecked={rolePerms.includes(k)} onChange={()=> togglePerm(k)}>
                    {k}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </CardBody></Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={isModalOpen} onClose={()=> setIsModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editUser ? t('users.modal.edit_user') : t('users.modal.new_user')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl isRequired>
                <FormLabel>{t('users.modal.username')}</FormLabel>
                <Input value={form.username} onChange={(e)=> setForm(f=>({...f, username:e.target.value}))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('users.email')}</FormLabel>
                <Input type="email" value={form.email} onChange={(e)=> setForm(f=>({...f, email:e.target.value}))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('users.modal.full_name')}</FormLabel>
                <Input value={form.full_name} onChange={(e)=> setForm(f=>({...f, full_name:e.target.value}))} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('users.modal.department')}</FormLabel>
                <Input value={form.department} onChange={(e)=> setForm(f=>({...f, department:e.target.value}))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('users.role')}</FormLabel>
                <Select value={form.role} onChange={(e)=> setForm(f=>({...f, role:e.target.value}))}>
                  {allRoles.map(r => (<option key={r} value={r}>{r}</option>))}
                </Select>
              </FormControl>
              {!editUser && (
                <FormControl isRequired>
                  <FormLabel>{t('users.modal.password')}</FormLabel>
                  <Input type="password" value={form.password} onChange={(e)=> setForm(f=>({...f, password:e.target.value}))} />
                </FormControl>
              )}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button onClick={()=> setIsModalOpen(false)}>{t('users.modal.cancel')}</Button>
              <Button colorScheme="brand" onClick={submitUser}>{t('users.modal.save')}</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Entity Management Modal */}
      <Modal isOpen={isEntityModalOpen} onClose={()=> setIsEntityModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('users.manage_entities_for', { defaultValue: 'Manage Entities for' })} {selectedUserForEntities?.full_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                {t('users.select_entities', { defaultValue: 'Select entities this user can access' })}
              </Text>
              {allEntities.map(entity => {
                const isAssigned = userEntities.some(ue => ue.id === entity.id);
                const isDefault = userEntities.some(ue => ue.id === entity.id && (ue.is_default === 1 || ue.is_default === true));
                return (
                  <HStack key={entity.id} justify="space-between" p={3} borderWidth="1px" borderRadius="md">
                    <HStack>
                      <Checkbox
                        isChecked={isAssigned}
                        onChange={() => toggleUserEntity(entity.id, false)}
                      >
                        <Text fontWeight={isDefault ? 'bold' : 'normal'}>
                          {entity.name}
                          {isDefault && (
                            <Text as="span" color="brand.500" ml={2}>
                              ({t('users.default', { defaultValue: 'Default' })})
                            </Text>
                          )}
                        </Text>
                      </Checkbox>
                    </HStack>
                    {isAssigned && !isDefault && (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => setDefaultEntity(entity.id)}
                      >
                        {t('users.set_default', { defaultValue: 'Set as Default' })}
                      </Button>
                    )}
                  </HStack>
                );
              })}
              {allEntities.length === 0 && (
                <Text color="gray.500">{t('users.no_entities', { defaultValue: 'No entities available' })}</Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={()=> {
              setIsEntityModalOpen(false);
              // Reload users list when modal closes
              loadUsers();
            }}>{t('common.close', { defaultValue: 'Close' })}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Users;


